/**
 * Lightweight loading skeleton shown immediately while lazy-loaded pages
 * download and parse. This keeps sidebar navigation feeling instant —
 * the tab switches right away and this skeleton fills the content area
 * until the real page is ready.
 */
export default function PageSkeleton() {
  return (
    <div className="p-4 pt-4 md:p-8 max-w-6xl mx-auto animate-pulse">
      {/* Title bar skeleton */}
      <div className="flex items-center justify-between mb-8">
        <div className="h-7 w-36 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-9 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>

      {/* Stats cards skeleton */}
      <div className="mb-10 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 md:p-5"
          >
            <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
            <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            <div className="h-3 w-20 bg-gray-100 dark:bg-gray-700/50 rounded" />
          </div>
        ))}
      </div>

      {/* Search bar skeleton */}
      <div className="mb-4 flex items-center gap-3">
        <div className="h-9 flex-1 max-w-sm bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>

      {/* Table skeleton */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="h-3 w-[9%] bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-[22%] bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-[11%] bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-[9%] bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-[13%] bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-[8%] bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-[12%] bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
        {/* Rows */}
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div
            key={i}
            className={`flex items-center gap-4 px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
              i % 2 === 1 ? 'bg-gray-50/60 dark:bg-white/[0.02]' : ''
            }`}
          >
            <div className="h-4 w-[9%] bg-gray-100 dark:bg-gray-700/60 rounded" />
            <div className="h-4 w-[22%] bg-gray-100 dark:bg-gray-700/60 rounded" />
            <div className="h-4 w-[11%] bg-gray-100 dark:bg-gray-700/60 rounded" />
            <div className="h-4 w-[9%] bg-gray-100 dark:bg-gray-700/60 rounded" />
            <div className="h-4 w-[13%] bg-gray-100 dark:bg-gray-700/60 rounded" />
            <div className="h-4 w-[8%] bg-gray-100 dark:bg-gray-700/60 rounded" />
            <div className="h-5 w-[10%] bg-gray-100 dark:bg-gray-700/60 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  )
}
